import { AfterViewInit, Component, HostListener, Injectable, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { cast, IQuestionnaireGroup } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import {
	AbstractSpecialQuestionComponent,
	ISpecialQuestionComponent
} from '../special-question.service';
import { AbstractReaktion } from '@workflow/components/questionnaire/questionnaire-item/special-question/abstract-reaction';
import { OptischeFolgereaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-folgereaktion/optische-folgereaktion-epilog.component';

@Injectable()
export class OptischeFolgereaktion extends AbstractReaktion {
	readonly COUNT = 10;
	public readonly prologueFullHeight = true;
	public readonly component = OptischeFolgereaktionComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'OptischeFolgereaktion';
	public readonly requiredDevices = [];

	title = 'Optische Folgereaktion';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: this.title,
		preface: [
			'<div class="d-flex flex-column justify-content-between align-items-centered height-100p"><div class="max-height-50p">Zur Bestimmung der optischen Folgereaktion bewegt sich im nächsten Test' +
			' ein Zeiger entgegen des Uhrzeigersinns in einem Kreis. Ihre Aufgabe ist es, diesen Zeiger nach der dritten Umrundung exakt auf 12 Uhr mit dem ' +
			'Buzzer zu stoppen. Der Zeiger wird durch Betätigen des Startknopfes jedes Mal neu gestartet. Der Test wird 10-mal wiederholt. Sie beginnen den ' +
			'Test durch Betätigen der “Test Starten” Schaltfläche auf der nächsten Seite.</div>' +
			'<div class="max-height-50p"><img class="object-fit-contain" src="assets/images/descriptions/Uhr.png" alt="uhr"/></div></div>'
		],
		items: [
			cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Optische Folgereaktion - Messwerte I - V',
				items: [
					{
						label: 'Messung 1',
						key: '0',
					}, {
						label: 'Messung 2',
						key: '1',
					}, {
						label: 'Messung 3',
						key: '2',
					}, {
						label: 'Messung 4',
						key: '3',
					}, {
						label: 'Messung 5',
						key: '4',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 333,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}), cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Optische Folgereaktion - Messwerte VI - X',
				items: [
					{
						label: 'Messung 6',
						key: '5',
					}, {
						label: 'Messung 7',
						key: '6',
					}, {
						label: 'Messung 8',
						key: '7',
					}, {
						label: 'Messung 9',
						key: '8',
					}, {
						label: 'Messung 10',
						key: '9',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 333,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}),
		],
		startText: 'actions.goToTest',
	};

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: OptischeFolgereaktionEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

@Component({
	selector: 'workflow-optische-folgereaktion',
	templateUrl: './optische-folgereaktion.component.html',
	styleUrls: ['./optische-folgereaktion.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class OptischeFolgereaktionComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, ISpecialQuestionComponent {
	public static count = 10;

	private reactPressed: boolean;
	private subscription: Subscription = new Subscription();
	private countdownTimer;

	private results: number[] = [];
	private completed: boolean;
	private startTime = 0;

	public buttonText = 'actions.starttest';
	private testStarted = false;
	public fehlEingabe: boolean;
	public countdown: number;
	public rotating: boolean;
	public stopAngle: number;

	constructor(@Optional() private workflowItemKey: WorkflowItemKeyDirective, router: Router, private renderer: Renderer2) {
		super(router);
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress($event: Event): void {
		if (!this.completed) {
			this.testStarted = true;
			if (this.rotating && this.countdown <= 0) {
				clearInterval(this.countdownTimer);
				const time = Math.abs($event.timeStamp - this.startTime - 3000); //3 Sekunden Wartezeit
				if (time > 1000/3) {
					this.fehlEingabe = true;
				} else {
					this.results.push(time);
				}
				this.updateResults();
				this.StopSpin();
			} else if (this.rotating && this.countdown > 0) {
				this.fehlEingabe = true;
				this.StopSpin();
				this.updateButtonText();
				return;
			} else {
				this.fehlEingabe = false;
				this.setTimer();
				this.StartSpin();
			}

			this.checkProgress();

		}
		this.reactPressed = true;
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-release', ['$event'])
	vitaReactRelease(): void {
		this.reactPressed = false;
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data && typeof(data[0]) === 'number' ? data as number[] : [];
				this.goToEpilog();
			}
		}));


		this.rotating = false;
		this.countdown = 2;
	}

	ngOnDestroy(): void {
		clearInterval(this.countdownTimer);
		this.subscription.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.results = [];
		const el = document.getElementById('rotating-circle');
		if (el.getAnimations().length > 0) {
			el.getAnimations()[0].pause();
		}
		this.checkProgress();
	}

	private checkProgress(): void {
		this.completed = this.results?.length === OptischeFolgereaktionComponent.count;
		this.updateButtonText();
		this.valid?.emit(this.completed);

		if (this.completed) {
			this.goToEpilog();
		}
	}

	private updateButtonText() {
		this.buttonText = this.rotating ? 'actions.stop' : this.testStarted ? 'actions.start' : 'actions.starttest';
	}

	private updateResults(): void {
		this.workflowItemKey.setProperty({...this.results});
	}

	private setTimer(): void {
		this.countdownTimer = setInterval(() => {
			if (this.countdown > 0 && performance.now() - this.startTime >= 3000 - this.countdown * 1000) {
				this.countdown--;
			}
		}, 16);
	}

	private StopSpin() {
		this.rotating = false;

		const el = document.getElementById('rotating-circle');
		if (el.getAnimations().length > 0) {
			el.getAnimations()[0].pause();
		}
	}

	private StartSpin() {
		const el = document.getElementById('rotating-circle');
		if (el.getAnimations().length > 0) {
			el.getAnimations()[0].currentTime = 0;
			el.getAnimations()[0].play();
		}
		this.countdown = 2;
		this.startTime = performance.now();
		this.rotating = true;
	}
}
